import type { DiagnosticReport, Observation } from '@medplum/fhirtypes';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';
import { DiagnosticReportDisplay } from './DiagnosticReportDisplay';
import { DocumentAttachment } from './DocumentAttachment';

export type DiagnosticReportSectionProps = {
  /** The DR to render. `undefined` after loading = no DR linked → section hides entirely. */
  diagnosticReport: DiagnosticReport | undefined;
  /** Resolved Observations referenced from `diagnosticReport.result[]` and `hasMember[]`. */
  observations: Map<string, Observation> | undefined;
  isLoading: boolean;
  /**
   * One or more underlying fetches (DR or referenced Observations) failed. Renders an inline
   * error in place of the table so the user knows results are incomplete rather than seeing
   * silently-dropped rows.
   */
  hasError?: boolean;
  /** When provided, each result row becomes clickable and calls this with the row's Observation. */
  onSelectObservation?: (observation: Observation) => void;
};

/**
 * Renders the lab-results section: header strip, results table, conclusion text, and the
 * lab's PDF report (`presentedForm`) if present. Hidden entirely when no DR is linked
 * (loading aside) — matches the gate pattern used by the other order-detail sections.
 */
export const DiagnosticReportSection = ({
  diagnosticReport,
  observations,
  isLoading,
  hasError = false,
  onSelectObservation,
}: DiagnosticReportSectionProps): React.ReactElement | null => {
  const { t } = useTranslation('records');
  // Per-instance so several sections can coexist on one page (a Procedure links a `report[]`
  // list) without colliding on `aria-labelledby`. Not derived from the report id — that would
  // still collide if the same report rendered twice, and is unavailable while loading.
  const headingId = useId();

  if (isLoading) {
    return (
      <section aria-labelledby={headingId} className="space-y-3">
        <h2 id={headingId} className="text-lg font-semibold">
          {t('diagnosticReport')}
        </h2>
        <Skeleton className="h-64 w-full" />
      </section>
    );
  }

  if (hasError) {
    return (
      <section aria-labelledby={headingId} className="space-y-3">
        <h2 id={headingId} className="text-lg font-semibold">
          {t('diagnosticReport')}
        </h2>
        <p role="alert" className="text-sm text-destructive">
          {t('diagnosticReportError')}
        </p>
      </section>
    );
  }

  if (!diagnosticReport) return null;

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-lg font-semibold">
        {t('diagnosticReport')}
      </h2>
      <DiagnosticReportDisplay
        diagnosticReport={diagnosticReport}
        observations={observations ?? new Map()}
        onSelectObservation={onSelectObservation}
      />
      {diagnosticReport.presentedForm?.map((attachment, idx) => (
        <DocumentAttachment key={`${diagnosticReport.id}-form-${idx}`} attachment={attachment} />
      ))}
    </section>
  );
};
